import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { equipmentDetails, equipmentCategoryList } from "../../data/equipmentData";
import { ArrowLeft, Download, ChevronRight, Ruler, Weight, Zap, ArrowUpDown, MoveHorizontal, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import Seo from "../Seo";

const MachineCard = ({ machine, index, heightLabel = "Arbeitshöhe" }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: Math.min(index * 0.05, 0.4) }}
      className="group bg-[#f5f5f7] rounded-3xl overflow-hidden hover:shadow-xl hover:shadow-black/5 transition-all duration-500"
    >
      {/* Image */}
      <div className="relative h-52 overflow-hidden bg-white flex items-center justify-center">
        <img
          src={machine.image}
          alt={machine.typ}
          className="max-h-full max-w-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
          onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1772889417579-5188aa6ff1bf?w=400&q=60'; }}
        />
        <div className="absolute top-3 right-3">
          <span className={`text-[10px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full shadow-sm ring-1 ring-black/5 ${
            machine.antrieb.toLowerCase().includes('elektro')
              ? 'bg-[#edf2ee] text-[#4a7c59]'
              : machine.antrieb.toLowerCase().includes('hybrid')
              ? 'bg-[#e9f2fe] text-blue-600'
              : 'bg-[#fef1e8] text-orange-600'
          }`}>
            {machine.antrieb}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-lg font-semibold text-[#1d1d1f] tracking-tight mb-4">
          {machine.typ}
        </h3>

        {/* Key Specs Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {machine.arbeitshoehe && machine.arbeitshoehe !== '-' && (
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-[#4a7c59]/8 flex items-center justify-center flex-shrink-0">
                <ArrowUpDown size={13} className="text-[#4a7c59]" />
              </div>
              <div>
                <p className="text-[10px] text-[#6e6e73]">{heightLabel}</p>
                <p className="text-xs font-semibold text-[#1d1d1f]">{machine.arbeitshoehe}</p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-[#4a7c59]/8 flex items-center justify-center flex-shrink-0">
              <Weight size={13} className="text-[#4a7c59]" />
            </div>
            <div>
              <p className="text-[10px] text-[#6e6e73]">Tragkraft</p>
              <p className="text-xs font-semibold text-[#1d1d1f]">{machine.tragkraft}</p>
            </div>
          </div>
          {machine.auslage && machine.auslage !== '-' && (
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-[#4a7c59]/8 flex items-center justify-center flex-shrink-0">
                <MoveHorizontal size={13} className="text-[#4a7c59]" />
              </div>
              <div>
                <p className="text-[10px] text-[#6e6e73]">Auslage</p>
                <p className="text-xs font-semibold text-[#1d1d1f]">{machine.auslage}</p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-[#4a7c59]/8 flex items-center justify-center flex-shrink-0">
              <Ruler size={13} className="text-[#4a7c59]" />
            </div>
            <div>
              <p className="text-[10px] text-[#6e6e73]">Breite</p>
              <p className="text-xs font-semibold text-[#1d1d1f]">{machine.transportbreite}</p>
            </div>
          </div>
        </div>

        {/* Expandable details */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-[#4a7c59] font-medium hover:underline mb-3 flex items-center gap-1"
        >
          {expanded ? 'Weniger anzeigen' : 'Alle Details'}
          <ChevronRight size={12} className={`transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </button>

        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="border-t border-[#e5e5e5] pt-3 mb-3"
          >
            <div className="space-y-2 text-xs">
              <Row label="Transporthöhe" value={machine.transporthoehe} />
              <Row label="Transportbreite" value={machine.transportbreite} />
              <Row label="Antrieb" value={machine.antrieb} />
              {machine.auslage && machine.auslage !== '-' && <Row label="Auslage" value={machine.auslage} />}
            </div>
          </motion.div>
        )}

        {/* Datenblatt */}
        {machine.datenblatt && (
          <a
            href={machine.datenblatt}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-xs font-medium text-[#4a7c59] hover:underline"
          >
            <Download size={13} />
            Datenblatt herunterladen
          </a>
        )}
      </div>
    </motion.div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-[#6e6e73]">{label}</span>
    <span className="text-[#1d1d1f] font-medium">{value}</span>
  </div>
);

const EquipmentDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const category = equipmentDetails[slug];
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#1d1d1f] mb-3">Kategorie nicht gefunden</h2>
          <button onClick={() => navigate('/')} className="text-[#4a7c59] hover:underline text-sm">
            Zurück zur Startseite
          </button>
        </div>
      </div>
    );
  }

  const drives = [...new Set(category.machines.map(m => m.antrieb))];
  const filteredMachines = filter === 'all'
    ? category.machines
    : category.machines.filter(m => m.antrieb === filter);

  // Get other categories for sidebar navigation
  const otherCategories = equipmentCategoryList.filter(c => c.slug !== slug);

  // Gabelstapler nutzen "Hubhöhe" statt "Arbeitshöhe"
  const heightLabel = category.name === "Gabelstapler" ? "Hubhöhe" : "Arbeitshöhe";

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title={`${category.name} mieten – Brinckmann Arbeitsbühnen`}
        description={`${category.name} bei Brinckmann Arbeitsbühnen mieten. ${category.description}`}
      />
      {/* Hero Header */}
      <div className="relative h-[50vh] min-h-[380px] overflow-hidden">
        <img
          src={category.heroImage}
          alt={category.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-[1200px] mx-auto px-6 pb-12 w-full">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-sm text-white/70 hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft size={16} />
              Zurück
            </button>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-6xl font-bold text-white tracking-tight mb-3"
            >
              {category.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-white/70 text-lg max-w-xl"
            >
              {category.description}
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-white/50 text-sm mt-3"
            >
              {category.machines.length} Geräte verfügbar
            </motion.p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1200px] mx-auto px-6 py-16">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Main Content */}
          <div className="flex-1">
            {/* Filter Bar */}
            {drives.length > 1 && (
              <div className="flex flex-wrap gap-2 mb-10">
                <button
                  onClick={() => setFilter('all')}
                  className={`text-xs font-medium px-4 py-2 rounded-full transition-all duration-300 ${
                    filter === 'all'
                      ? 'bg-[#1d1d1f] text-white'
                      : 'bg-[#f5f5f7] text-[#6e6e73] hover:bg-[#e5e5e5]'
                  }`}
                >
                  Alle ({category.machines.length})
                </button>
                {drives.map(d => (
                  <button
                    key={d}
                    onClick={() => setFilter(d)}
                    className={`text-xs font-medium px-4 py-2 rounded-full transition-all duration-300 ${
                      filter === d
                        ? 'bg-[#1d1d1f] text-white'
                        : 'bg-[#f5f5f7] text-[#6e6e73] hover:bg-[#e5e5e5]'
                    }`}
                  >
                    {d} ({category.machines.filter(m => m.antrieb === d).length})
                  </button>
                ))}
              </div>
            )}

            {/* Machine Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredMachines.map((machine, index) => (
                <MachineCard key={machine.typ} machine={machine} index={index} heightLabel={heightLabel} />
              ))}
            </div>

            {/* Comparison Table */}
            <div className="mt-16">
              <h2 className="text-2xl font-bold text-[#1d1d1f] tracking-tight mb-6">
                Übersicht aller {category.name}
              </h2>
              <div className="overflow-x-auto rounded-2xl border border-[#e5e5e5]">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-[#f5f5f7]">
                      <th className="text-xs font-semibold text-[#6e6e73] uppercase tracking-wider px-5 py-3">Gerät</th>
                      <th className="text-xs font-semibold text-[#6e6e73] uppercase tracking-wider px-5 py-3">{heightLabel}</th>
                      <th className="text-xs font-semibold text-[#6e6e73] uppercase tracking-wider px-5 py-3">Tragkraft</th>
                      <th className="text-xs font-semibold text-[#6e6e73] uppercase tracking-wider px-5 py-3">Antrieb</th>
                      <th className="text-xs font-semibold text-[#6e6e73] uppercase tracking-wider px-5 py-3">Breite</th>
                      <th className="text-xs font-semibold text-[#6e6e73] uppercase tracking-wider px-5 py-3">Höhe</th>
                      {category.machines.some(m => m.datenblatt) && (
                        <th className="text-xs font-semibold text-[#6e6e73] uppercase tracking-wider px-5 py-3">Datenblatt</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMachines.map((m, i) => (
                      <tr key={m.typ} className={`border-t border-[#f0f0f0] ${i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'} hover:bg-[#f5f5f7] transition-colors`}>
                        <td className="text-sm font-medium text-[#1d1d1f] px-5 py-3">{m.typ}</td>
                        <td className="text-sm text-[#424245] px-5 py-3">{m.arbeitshoehe}</td>
                        <td className="text-sm text-[#424245] px-5 py-3">{m.tragkraft}</td>
                        <td className="text-sm text-[#424245] px-5 py-3">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                            m.antrieb.toLowerCase().includes('elektro')
                              ? 'bg-[#4a7c59]/10 text-[#4a7c59]'
                              : m.antrieb.toLowerCase().includes('hybrid')
                              ? 'bg-blue-500/10 text-blue-600'
                              : 'bg-orange-500/10 text-orange-600'
                          }`}>{m.antrieb}</span>
                        </td>
                        <td className="text-sm text-[#424245] px-5 py-3 whitespace-nowrap">{m.transportbreite}</td>
                        <td className="text-sm text-[#424245] px-5 py-3 whitespace-nowrap">{m.transporthoehe}</td>
                        {category.machines.some(m2 => m2.datenblatt) && (
                          <td className="px-5 py-3">
                            {m.datenblatt ? (
                              <a href={m.datenblatt} target="_blank" rel="noopener noreferrer" className="text-[#4a7c59] hover:underline text-xs flex items-center gap-1">
                                <Download size={12} /> PDF
                              </a>
                            ) : <span className="text-xs text-[#aeaeb2]">–</span>}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:w-[260px] flex-shrink-0">
            <div className="sticky top-20">
              <h3 className="text-xs font-semibold text-[#6e6e73] uppercase tracking-wider mb-4">
                Weitere Kategorien
              </h3>
              <div className="flex flex-col gap-1">
                {otherCategories.map(cat => (
                  <Link
                    key={cat.slug}
                    to={`/geraete/${cat.slug}`}
                    className="group flex items-center justify-between px-4 py-3 rounded-xl hover:bg-[#f5f5f7] transition-colors"
                  >
                    <span className="text-sm text-[#1d1d1f] group-hover:text-[#4a7c59] transition-colors">
                      {cat.name}
                    </span>
                    <ArrowUpRight size={14} className="text-[#aeaeb2] group-hover:text-[#4a7c59] transition-colors" />
                  </Link>
                ))}
              </div>

              {/* CTA */}
              <div className="mt-8 bg-[#f5f5f7] rounded-2xl p-6">
                <h4 className="text-sm font-semibold text-[#1d1d1f] mb-2">
                  Beratung gewünscht?
                </h4>
                <p className="text-xs text-[#6e6e73] mb-4 leading-relaxed">
                  Wir helfen Ihnen gerne bei der Auswahl des passenden Gerätes.
                </p>
                <a
                  href="tel:+492871242660"
                  className="flex items-center justify-center gap-2 bg-[#4a7c59] hover:bg-[#3d6a4a] text-white text-xs font-medium py-3 rounded-xl transition-all duration-300 w-full"
                >
                  02871 / 242 660
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquipmentDetailPage;
